import {Col, Image, Row} from 'react-bootstrap';
import {GiAirplaneDeparture} from 'react-icons/gi';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CustomImageHandler} from '../../../../../../../../shared/components/CustomImageHandler';
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material';
import {Totals} from '../contract/Totals';
import {formatDate, getTotals} from '../../../../../../../../shared/functions/Functions';
import {ContractHistoryAdditionalItemsTable} from './ContractHistoryAdditionalItemsTable';
import {ContractHistoryOfferPlatformTable} from './ContractHistoryOfferPlatformTable';
import {ContractHistoryOfferHeader} from './ContractHistoryOfferHeader';
import {useSelector} from 'react-redux';
import {IAllStates} from '../../../../../../../../store/rootReducer';
import {TotalTypes} from '../../../../../../../../enum/TotalTypes';
import {IContract} from '../../../../../../../../model/shared/IContract';

interface INegotiationHistoryDetailProps {
    contract?: IContract;
}

const ContractHistoryDetail = ({contract}: INegotiationHistoryDetailProps) => {
    const [expended, setExpended] = useState(false);
    const {t} = useTranslation();
    const {
        isTransparentPricing, campaign, isCharity,
    } = useSelector((state: IAllStates) => state.contractBrand);
    const feePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = getTotals(contract?.additionalContractItems, contract?.totalPlatforms);
    const grandTotalForDisplay = isTransparentPricing ? contract?.totalWithFee : subTotalSum;
    return (
        <Accordion className="mt-1" expanded={expended}>
            <AccordionSummary className="p-0 border-0" onClick={() => setExpended(!expended)}>
                <Row className="default-radius bg-light py-2 px-4">
                    <Col lg={6} className="d-flex align-items-center px-0">
                        {contract?.counterContractBy ?
                            <CustomImageHandler
                                altTag={contract?.counterContractBy?.profilePhoto?.name ?? ''}
                                classes="rounded-img-sm"
                                thumbnailPath={contract?.counterContractBy.isSupervisor ? '' : contract?.counterContractBy?.profilePhoto?.thumbnail}
                                photoPath={contract?.counterContractBy.isSupervisor ? '/icons/logoSmall.svg' : contract?.counterContractBy?.profilePhoto?.thumbnail ?? ''}
                            /> :
                            <CustomImageHandler
                                altTag={contract?.user?.profilePhoto?.name ?? ''}
                                classes="rounded-img-sm"
                                thumbnailPath={contract?.user?.profilePhoto?.thumbnail}
                                photoPath={contract?.user?.profilePhoto?.thumbnail ?? ''}
                            />
                        }
                        <div>
                            {contract?.counterContractBy ?
                                <h6 className="ml-3 mb-1">
                                    {contract?.counterContractBy.isSupervisor ? 'Customer Success by i.v.' : !!contract?.counterContractBy?.displayName ? contract?.counterContractBy?.displayName : (contract?.counterContractBy?.firstName + ' ' + contract?.counterContractBy?.lastName)}
                                </h6> :
                                <h6 className="ml-3 mb-1">
                                    {!!contract?.user.displayName ? contract?.user.displayName : (contract?.user?.firstName + ' ' + contract?.user?.lastName)}
                                </h6>
                            }
                            <h6 className="ml-3 mb-0">{formatDate(contract?.createdAt ?? '')} - {contract?.createdAt.substring(11, 16)}</h6>
                        </div>
                    </Col>
                    <Col lg={6} className="d-flex align-items-center justify-content-between py-3 px-0 p-lg-0 ">
                        <div>
                            <div className="d-flex align-items-center">
                                <h5 className="mb-0 text-primary euro-symbol">{grandTotalForDisplay}</h5>
                            </div>
                            {!!contract?.additionalContractItems?.length &&
                                <span className="text-muted font-10">
                                    <GiAirplaneDeparture className="text-primary mr-1 font-14"/>
                                    {t('brand.campaign.book.contract.additional_costs_included')}
                                </span>
                            }
                        </div>
                        {expended ?
                            <div className="text-uppercase text-muted d-flex align-items-center">
                                <p className="mb-0">{t('general.button.hide_details')}</p>
                                <Image className="ml-2" alt="image-up" src="/icons/img_arrow_up.svg"/>
                            </div>
                            :
                            <div className="text-uppercase text-muted d-flex align-items-center">
                                <p className="mb-0">{t('general.button.see_details')}</p>
                                <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg"/>
                            </div>
                        }
                    </Col>
                </Row>
            </AccordionSummary>
            <AccordionDetails className="p-0 ">
                <ContractHistoryOfferHeader personalOffer={contract?.personalOffer} createdAt={contract?.createdAt ?? ''}
                                            userId={contract?.user?.id} counterOfferById={contract?.counterContractBy?.id}/>
                <ContractHistoryOfferPlatformTable totalPlatforms={contract?.totalPlatforms}/>
                {(contract?.additionalContractItems?.length !== 0) &&
                    <>
                        <ContractHistoryAdditionalItemsTable additionalItems={contract?.additionalContractItems}/>
                        <Totals title={TotalTypes.ADDITIONAL_ITEMS}
                                price={getTotals(contract?.additionalContractItems)}
                                displayPlus={true}
                        />
                    </>
                }
                {(isTransparentPricing && !isCharity) &&
                    <div className="w-100 mt-3">
                        <Totals
                            title={TotalTypes.SUBTOTAL}
                            price={subTotalSum}
                            feePercentage={100 - feePercentage}
                        />
                    </div>
                }
                {(isTransparentPricing) &&
                    <div className="w-100 mt-3">
                        <Totals
                            title={TotalTypes.FEE}
                            price={contract?.feeAmount ?? 0}
                            feePercentage={feePercentage}
                            displayPlus={true}
                        />
                    </div>
                }
                <div className="mt-3">
                    <Totals title={TotalTypes.GRAND}
                            price={grandTotalForDisplay}
                            feePercentage={isTransparentPricing ? 100 : undefined}
                    />
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

export default ContractHistoryDetail;
